import { useState } from "react";
import { useForm } from "react-hook-form";
import { MapPin, Phone, Mail, Clock, Loader2, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { useSite } from "../context/ContentContext";
import { submitContact } from "../lib/api";

export default function Contact() {
  const { company: COMPANY, services: SERVICES } = useSite();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await submitContact(data);
      toast.success("Thank you! Your inquiry has been received. We'll be in touch shortly.");
      reset();
    } catch (e) {
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition";

  return (
    <div data-testid="contact-page">
      <Seo title="Contact Us" description="Contact Domaleo Enterprise in Accra, Ghana. Request a quote for import & export, IT, agriculture, logistics or general supply services." />

      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-px mx-auto max-w-7xl">
          <Reveal>
            <p className="overline text-accent">Contact Us</p>
            <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance">Let's discuss your next project</h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-200 leading-relaxed">Reach out for a tailored quote or any inquiry. Our team responds promptly.</p>
          </Reveal>
        </div>
      </section>

      <section className="container-px mx-auto max-w-7xl py-16 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* form */}
          <Reveal>
            <form onSubmit={handleSubmit(onSubmit)} className="rounded-3xl border border-border bg-card p-7 lg:p-9" data-testid="contact-form" noValidate>
              <h2 className="font-heading text-2xl font-semibold">Send us a message</h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <input className={field} data-testid="contact-name-input" {...register("name", { required: "Name is required", minLength: { value: 2, message: "Too short" } })} />
                  {errors.name && <p className="mt-1 text-xs text-destructive" data-testid="error-name">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium">Email *</label>
                  <input type="email" className={field} data-testid="contact-email-input" {...register("email", { required: "Email is required", pattern: { value: /\S+@\S+\.\S+/, message: "Enter a valid email" } })} />
                  {errors.email && <p className="mt-1 text-xs text-destructive" data-testid="error-email">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <input className={field} data-testid="contact-phone-input" {...register("phone")} />
                </div>
                <div>
                  <label className="text-sm font-medium">Company</label>
                  <input className={field} data-testid="contact-company-input" {...register("company")} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium">Service Needed</label>
                  <select className={field} data-testid="contact-service-select" defaultValue="" {...register("service")}>
                    <option value="" disabled>Select a service</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium">Message *</label>
                  <textarea rows={5} className={field} data-testid="contact-message-input" {...register("message", { required: "Message is required", minLength: { value: 5, message: "Tell us a little more" } })} />
                  {errors.message && <p className="mt-1 text-xs text-destructive" data-testid="error-message">{errors.message.message}</p>}
                </div>
              </div>
              <button type="submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60" data-testid="contact-submit-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send Message <Send className="h-4 w-4" /></>}
              </button>
            </form>
          </Reveal>

          {/* info + map */}
          <Reveal delay={0.1} className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-6">
                <MapPin className="h-6 w-6 text-primary" />
                <h3 className="mt-3 font-heading font-semibold">Office</h3>
                <p className="mt-1 text-sm text-muted-foreground">{COMPANY.address}</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <Phone className="h-6 w-6 text-primary" />
                <h3 className="mt-3 font-heading font-semibold">Phone</h3>
                <div className="mt-1 space-y-0.5">
                  {((COMPANY.phones && COMPANY.phones.length) ? COMPANY.phones : [COMPANY.phone]).map((p) => (
                    <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="contact-phone">{p}</a>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <Mail className="h-6 w-6 text-primary" />
                <h3 className="mt-3 font-heading font-semibold">Email</h3>
                <p className="mt-1 text-sm text-muted-foreground break-all">{COMPANY.email}</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <Clock className="h-6 w-6 text-primary" />
                <h3 className="mt-3 font-heading font-semibold">Business Hours</h3>
                <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                  {COMPANY.hours.map((h) => <li key={h.day}>{h.day}: {h.time}</li>)}
                </ul>
              </div>
            </div>

            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-transform hover:scale-[1.02]" data-testid="contact-whatsapp-btn">
              <MessageCircle className="h-5 w-5" /> Chat with us on WhatsApp
            </a>

            <div className="overflow-hidden rounded-2xl border border-border">
              <iframe
                title="Domaleo Enterprise location"
                src={COMPANY.mapEmbed}
                className="h-72 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
